// Patch 008.6 → 008.7 — chassis is now a single sticky header bar
// (brand + switcher + inlined-and-centered 7-section nav + action
// stubs). The pre-008.7 standalone <Nav /> strip and <SubNav /> below
// the header have both been removed; sub-nav (Plan: Command Board |
// Facts) renders inside each Plan-section page via the in-page
// .hcc-page-tabs strip.

import { Suspense } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Footer } from './Footer'
import { Header } from './Header'
import { SECTIONS, type SectionId } from './Nav'
import { Skeleton } from './Skeleton'

const VALID_SECTIONS: ReadonlySet<string> = new Set(SECTIONS.map((s) => s.id))

function parseUrl(pathname: string): {
  projectId: string | undefined
  section: SectionId | null
} {
  // Patch 010.E.4 — top-level `/profiles` and `/profiles/:profileId` are
  // cross-project routes; they highlight the `profiles` section in the nav
  // and don't carry a project context.
  if (pathname === '/profiles' || pathname.startsWith('/profiles/')) {
    return { projectId: undefined, section: 'profiles' }
  }
  // Patch 015.3 — /admin/command-board/:projectId is the demoted Command
  // Board's admin-internal home. It carries a project context (so the
  // Header switcher + project actions stay live) but highlights no
  // section in the nav — it is a diagnostic surface, not a workflow tab.
  const adminCb = pathname.match(/^\/admin\/command-board\/([^/]+)/)
  if (adminCb) {
    return { projectId: adminCb[1], section: null }
  }
  const m = pathname.match(/^\/projects\/([^/]+)(?:\/([^/]+))?/)
  const projectId = m?.[1]
  const seg = m?.[2]
  const section = seg && VALID_SECTIONS.has(seg) ? (seg as SectionId) : null
  return { projectId, section }
}

export function Layout(): JSX.Element {
  const { pathname } = useLocation()
  const { projectId, section } = parseUrl(pathname)

  return (
    <div className="hcc-shell">
      <Header projectId={projectId} activeSection={section} />
      <main className="hcc-main">
        {/* Wave-7 B4 — the Suspense boundary for route-level code-split
            lazy() pages sits here, inside the Outlet, so the header + nav
            shell stays mounted while a page chunk loads. */}
        <Suspense fallback={<Skeleton variant="card" count={3} />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}
